import {Component, OnInit} from '@angular/core';
import {SubmitForm} from '../../../sdk/interface/form/submitForm';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {AlertService} from '../../../sdk/service/alert.service';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {ActivatedRoute, Router} from '@angular/router';
import {SyncValidators} from '../../../sdk/validator/SyncValidators';
import {
    Account,
    BusinessDetails,
    QrCodeAmountBounds,
    QrCodeCreate,
    QrCodeUpdate,
    WebBusinessService,
    WebQRService
} from 'web-angular-client';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {AsyncValidators} from '../../../sdk/validator/AsyncValidators';
import {ROUTES} from '../../../app.routes';
import {SubscriptionService} from '../../../sdk/service/subscription.service';
import {ConfirmationService} from '../../../sdk/service/confirmation.service';
import {Helper} from '../../../sdk/util/helper';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {DropdownItem} from '../../../sdk/interface/data/DropdownItem';
import {ConstantsUtil} from '../../../sdk/util/constantsUtil';
import {finalize} from 'rxjs';

@Component({
    selector: 'app-edit-qr-code',
    templateUrl: './edit-qr-code.component.html',
    styleUrls: ['./edit-qr-code.component.scss'],
    standalone: false
})
export class EditQrCodeComponent extends SubmitForm implements OnInit
{
    minimumQrCodeAmount: number;
    maximumQrCodeAmount: number;
    qrCodeId: number;
    isMultiPremiumEnabled = false;

    readonly multiPremiumOptions: DropdownItem[] = ConstantsUtil.MULTI_PREMIUM_OPTIONS;

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private webQrService: WebQRService,
        private ngxLoader: NgxUiLoaderService,
        private route: ActivatedRoute,
        protected businessService: WebBusinessService,
        private subscriptionService: SubscriptionService,
        private confirmationService: ConfirmationService)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
    }

    get exactAmountEnabledLabel(): string
    {
        return this.isExactAmountEnabled ? 'YES' : 'NO';
    }

    get isExactAmountEnabled(): boolean
    {
        return !!this.form?.value?.exactAmountEnabled;
    }

    get sendPaymentNotificationLabel(): string
    {
        return this.isSendPaymentNotification ? 'YES' : 'NO';
    }

    get isSendPaymentNotification()
    {
        return this.form?.value?.notification;
    }

    get isFormInvalid(): boolean
    {
        return !this.form.valid;
    }

    get isSubscriptionValid(): boolean
    {
        return this.subscriptionService.isSubscriptionValid;
    }

    get getTooltipText(): string
    {
        if (this.isGuestAdminUser())
        {
            return 'Guest accounts cannot create QR codes.';
        }
        else if (!this.isSubscriptionValid)
        {
            return 'Please pay your subscription fee before attempting to create a QR code.';
        }
        return '';
    }

    get isValidatingDescription(): boolean
    {
        return this.form.controls['qrCodeDescription'].pending;
    }

    ngOnInit(): void
    {
        this.ngxLoader.start();
        this.route.params.subscribe(params =>
        {
            this.qrCodeId = params['id'] ?? null;
        });

        this.form = this.formBuilder.group({
            qrCodeDescription: [
                '',
                [Validators.required, Validators.maxLength(30)],
                AsyncValidators.validateQrCodeDescription(this.webQrService, this.qrCodeId)
            ],
            minimumAmount: ['0.00'],
            maximumAmount: ['0.00'],
            multiPremiumOption: [
                1,
                [Validators.required, Validators.min(1), Validators.max(12), Validators.pattern('^\\d+$')]
            ],
            exactAmountEnabled: [false, [Validators.required]],
            exactAmount: ['0.00'],
            notification: [false, [Validators.required]],
            smsNotificationNumber: ['']
        });

        this.businessService.getBusinessDetails().subscribe((businessDetails: BusinessDetails) =>
        {
            this.isMultiPremiumEnabled = businessDetails.multiPremiumEnabled;

            if (this.isMultiPremiumEnabled)
            {
                this.f.exactAmount.valueChanges.subscribe((exactAmount) => this.updateQrMinMax(exactAmount, null));
                this.f.multiPremiumOption.valueChanges.subscribe((multiPremiumOption) => this.updateQrMinMax(
                    null,
                    multiPremiumOption));
            }

            this.loadQrCodeAmountBounds();
        });

        this.f.exactAmountEnabled.valueChanges.subscribe((isExactAmountEnabled) => this.applyExactAmountDependencies(
            isExactAmountEnabled));

        if (!!this.qrCodeId)
        {
            this.loadQrCodeDetails(this.qrCodeId);
        }

        this.applyExactAmountDependencies(this.isExactAmountEnabled);

        this.ngxLoader.stop();
    }

    submitQrCode(): void
    {
        this.onSubmit();
    }

    submit(): void
    {
        this.ngxLoader.start();
        this.loading = true;

        if (this.qrCodeId)
        {
            this.updateQrCode();
        }
        else
        {
            this.createQrCode();
        }
    }

    updateSmsNotificationValidation(): void
    {
        if (this.isSendPaymentNotification)
        {
            this.f.smsNotificationNumber.addValidators([Validators.required, SyncValidators.validateMobileNumber()]);
        }
        else
        {
            this.f.smsNotificationNumber.clearValidators();
        }
        this.f.smsNotificationNumber.updateValueAndValidity();
    }

    cancel(): void
    {
        this.confirmationService.cancelConfirmation('QR code', 'Any changes made will be' +
            ' lost').then((confirmed) =>
        {
            if (confirmed)
            {
                this.router.navigate([ROUTES.SECURE.QR.LIST.path]).then();
            }
        });
    }

    /**
     * Updates the minimum and maximum QR amounts based on the exact amount and multiPremiumOption value.
     */
    private updateQrMinMax(exactValue: number | null, multiPremiumOptionValue: number | null): void
    {
        const {exactAmount, multiPremiumOption, minimumAmount, maximumAmount} = this.f;
        exactValue = +exactValue || +exactAmount.value;
        multiPremiumOptionValue = +multiPremiumOptionValue || +multiPremiumOption.value;

        if (!(this.isMultiPremiumEnabled && this.isExactAmountEnabled && exactValue && multiPremiumOptionValue))
        {
            return;
        }

        const maxQrAmount = exactValue * multiPremiumOptionValue;

        minimumAmount.setValue(exactValue.toFixed(2));
        maximumAmount.setValue(maxQrAmount.toFixed(2));

        minimumAmount.markAsTouched();
        maximumAmount.markAsTouched();
        minimumAmount.updateValueAndValidity();
        maximumAmount.updateValueAndValidity();
        this.form.updateValueAndValidity();
    }

    /**
     * Applies dependencies between the "exact amount" and "multi premium option" fields.
     *
     * If both `isExactAmountEnabled` and `isMultiPremiumEnabled` are true,
     * the `multiPremiumOption` field is enabled; otherwise, it is disabled.
     */
    private applyExactAmountDependencies(isExactAmountEnabled: boolean): void
    {
        const {multiPremiumOption, minimumAmount, maximumAmount} = this.f;
        const shouldEnable = isExactAmountEnabled && this.isMultiPremiumEnabled;

        if (shouldEnable)
        {
            multiPremiumOption.enable();
            minimumAmount.disable();
            maximumAmount.disable();
        }
        else
        {
            multiPremiumOption.disable();
            minimumAmount.enable();
            maximumAmount.enable();
        }

        this.form.updateValueAndValidity();
    }

    private mapFormForSubmission(): QrCodeCreate | QrCodeUpdate
    {
        const exactAmountValue = this.isExactAmountEnabled ? Helper.cleanCurrencyValue(this.f.exactAmount.value) : null;
        const maximumAmountValue = this.isExactAmountEnabled
            ? (exactAmountValue * (this.isMultiPremiumEnabled ? this.f.multiPremiumOption.value : 1))
            : Helper.cleanCurrencyValue(this.f.maximumAmount?.value);

        return {
            description: this.f.qrCodeDescription?.value ?? null,
            minimumAmount: Helper.convertToMinorDenomination(
                this.isExactAmountEnabled ? exactAmountValue : Helper.cleanCurrencyValue(this.f.minimumAmount?.value)
            ),
            maximumAmount: Helper.convertToMinorDenomination(maximumAmountValue),
            notificationMobileNumber: this.isSendPaymentNotification ? (this.f.smsNotificationNumber.value ?? null) : null,
            multiPremium: (this.isMultiPremiumEnabled && this.isExactAmountEnabled) ? this.f.multiPremiumOption.value || null : null,
        };
    }

    private createQrCode(): void
    {
        const form = this.mapFormForSubmission();

        this.webQrService.createQR(form)
            .pipe(finalize(() =>
            {
                this.ngxLoader.stop();
                this.loading = false;
            }))
            .subscribe({
                next: (createdQrCode: Account) => this.router.navigate([
                    ROUTES.SECURE.QR.DETAILS.pathSansParameters,
                    createdQrCode.id
                ]).then()
                ,
                error: (error) => this.alertService.error(error.error.errorMessage)
            });
    }

    private updateQrCode(): void
    {
        this.webQrService.updateQrAccount(this.qrCodeId, this.mapFormForSubmission())
            .pipe(finalize(() =>
            {
                this.ngxLoader.stop();
                this.loading = false;
            })).subscribe({
            next: (updatedQrCode: Account) => this.router.navigate([
                ROUTES.SECURE.QR.DETAILS.pathSansParameters,
                updatedQrCode.id
            ]).then()
            ,
            error: (error) => this.alertService.error(error.error.errorMessage)
        });
    }

    private loadQrCodeAmountBounds()
    {
        this.webQrService.getQRAmountBounds()
            .pipe(finalize(() => this.ngxLoader.stop()))
            .subscribe({
                next: (qrCodeAmountBounds: QrCodeAmountBounds) =>
                {
                    this.minimumQrCodeAmount = Helper.convertToMajorDenomination(qrCodeAmountBounds.minimumAmount);
                    this.maximumQrCodeAmount = Helper.convertToMajorDenomination(qrCodeAmountBounds.maximumAmount);

                    [this.f.minimumAmount, this.f.maximumAmount, this.f.exactAmount].forEach(control =>
                    {
                        control.setValidators([
                            SyncValidators.minimumTotalValidator(this.minimumQrCodeAmount),
                            SyncValidators.maximumTotalValidator(this.maximumQrCodeAmount)
                        ]);
                        control.updateValueAndValidity({emitEvent: false});
                    });

                    if (!this.qrCodeId)
                    {
                        this.form.patchValue({
                            minimumAmount: this.minimumQrCodeAmount,
                            maximumAmount: this.maximumQrCodeAmount,
                            exactAmount: this.minimumQrCodeAmount
                        });
                    }

                    if (this.isMultiPremiumEnabled)
                    {
                        this.form.addValidators(
                            SyncValidators.maximumMultiplesOfTotalValidator(
                                this.maximumQrCodeAmount,
                                false
                            )
                        );
                    }
                },
                error: (error) => this.alertService.error(error.error.errorMessage)
            });
    }

    private loadQrCodeDetails(qrCodeId: number): void
    {
        this.webQrService.getQrAccountById(qrCodeId)
            .pipe(finalize(() => this.ngxLoader.stop()))
            .subscribe({
                next: (qrCode: Account) => this.populateFormFromLoadedQrCode(qrCode),
                error: (error) =>
                {
                    this.alertService.error(error, true);
                    this.router.navigate([this.routes.SECURE.QR.LIST.path]).then();
                }
            });
    }

    private populateFormFromLoadedQrCode(qrCode: Account): void
    {
        let exactAmountEnabled = qrCode.maximumAmount === qrCode.minimumAmount || !!qrCode.multiPremium;
        this.form.patchValue({
            qrCodeDescription: qrCode.description,
            multiPremiumOption: qrCode.multiPremium,
            exactAmountEnabled: exactAmountEnabled,
            exactAmount: Helper.convertToMajorDenomination(qrCode.minimumAmount).toFixed(2),
            minimumAmount: Helper.convertToMajorDenomination(qrCode.minimumAmount).toFixed(2),
            maximumAmount: Helper.convertToMajorDenomination(qrCode.maximumAmount).toFixed(2),
            notification: !!qrCode.notificationMobileNumber,
            smsNotificationNumber: qrCode.notificationMobileNumber ?? ''
        });

        this.applyExactAmountDependencies(exactAmountEnabled);
        this.updateSmsNotificationValidation();
        this.updateQrMinMax(null, null);
    }
}
